import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
	Box,
	Typography,
	CircularProgress
} from '@mui/material';
import { TemperatureIcon, humidityIcon, DoorIcon } from '../assets/icons';
import skytronLogo from '../assets/skytron.svg';
import dipsLogo from '../assets/logo.png';

const THEME_LOGOS = {
	dips: dipsLogo,
	reveal: skytronLogo
};

const OR_THEMES = {
	dips: {
		pageBackground: '#222D62',
		frameBackground: '#161D40',
		headerBackground: '#222D62',
		headerBorder: '#121734',
		cardBackground: '#f8f9fb',
		borderSoft: 'rgba(255, 255, 255, 0.2)',
		textPrimary: '#ffffff',
		textSecondary: '#ffffffd5',
		cardLabel: '#22BCFC',
		cardValue: '#22BCFC',
		sensorName: '#888888',
		accent: '#ffffff',
		success: '#4caf50',
		warning: '#ff9800',
		error: '#f44336',
		appLogoAlt: 'DIPS'
	},
	reveal: {
		pageBackground: '#226BB5',
		frameBackground: '#1d4574',
		headerBackground: '#226BB5',
		headerBorder: '#152F54',
		cardBackground: '#f8f9fb',
		borderSoft: 'rgba(255, 255, 255, 0.2)',
		textPrimary: '#ffffff',
		textSecondary: '#ffffffd5',
		cardLabel: '#199CFF',
		cardValue: '#199CFF',
		sensorName: '#888888',
		accent: '#ffffff',
		success: '#4caf50',
		warning: '#ff9800',
		error: '#f44336',
		appLogoAlt: 'skytron reveal'
	}
};

const getORTheme = (themeKey) => OR_THEMES[themeKey] ?? OR_THEMES.dips;

const getThemeFromDomain = () => {
	const subDomain = window.location.hostname.split('.')[0];
	return subDomain.indexOf('reveal') !== -1 ? 'reveal' : null;
};

const formatMetricValue = (value) => {
	return value === null || value === undefined || value === '' ? '--' : String(value);
};

const formatDuration = (seconds) => {
	if (seconds === null || seconds === undefined || seconds === '') return '--';
	
	const totalSeconds = Math.round(Number(seconds));
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const secs = totalSeconds % 60;
	
	if (hours > 0) {
		return `${hours}h ${minutes}m ${secs}s`;
	} else if (minutes > 0) {
		return `${minutes}m ${secs}s`;
	} else {
		return `${secs}s`;
	}
};

const DoorCumulativeCard = ({ icon: Icon, cumulativeCount, cumulativeDuration, avgDuration, totalCards, theme }) => {
	const cardHeight = totalCards === 1 ? 350 : totalCards <= 4 ? 270 : 250;

	// Independent font sizing — 2 tiers: 2-4 cards, 5+ cards
	const fs = totalCards <= 4
		? { sensorName: 32, label: 22, value: 48, subLabel: 18, subValue: 26, iconBox: 80, iconSize: 44, p: 2, gap: 1.5, subGap: 3 }
		: { sensorName: 24, label: 20, value: 44, subLabel: 16, subValue: 22, iconBox: 72, iconSize: 40, p: 1.5, gap: 1, subGap: 2 };

	return (
		<Box
			sx={{
				backgroundColor: theme.cardBackground,
				borderRadius: '20px',
				px: { xs: 3, sm: 4, md: 4 },
				py: { xs: 3, sm: 3, md: 3 },
				border: '2px solid rgba(62, 95, 167, 0.15)',
				display: 'flex',
				flexDirection: 'column',
				gap: fs.gap,
				minHeight: cardHeight,
				maxHeight: cardHeight,
				width: '100%',
				overflow: 'hidden'
			}}
		>
			<Typography sx={{ color: theme.sensorName, fontSize: fs.sensorName, fontWeight: 500, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
				Door Openings
			</Typography>

			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flex: 1 }}>
				<Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: fs.gap }}>
					<Box>
						<Typography sx={{ color: theme.cardLabel, fontSize: fs.label, fontWeight: 700, lineHeight: 1.2 }}>
							Total/Day
						</Typography>
						<Typography sx={{ color: theme.cardValue, fontSize: fs.value, fontWeight: 700, lineHeight: 1.05 }}>
							{formatMetricValue(cumulativeCount)}
						</Typography>
					</Box>
					<Box sx={{ display: 'flex', gap: fs.subGap }}>
						<Box>
							<Typography sx={{ color: theme.cardLabel, fontSize: fs.subLabel, fontWeight: 600, lineHeight: 1.2 }}>
								Duration/Day
							</Typography>
							<Typography sx={{ color: theme.cardValue, fontSize: fs.subValue, fontWeight: 600, lineHeight: 1.05 }}>
								{formatDuration(cumulativeDuration)}
							</Typography>
						</Box>
						<Box>
							<Typography sx={{ color: theme.cardLabel, fontSize: fs.subLabel, fontWeight: 600, lineHeight: 1.2 }}>
								Avg/Day
							</Typography>
							<Typography sx={{ color: theme.cardValue, fontSize: fs.subValue, fontWeight: 600, lineHeight: 1.05 }}>
								{formatDuration(avgDuration)}
							</Typography>
						</Box>
					</Box>
				</Box>

				<Box sx={{
					backgroundColor: '#4A6BA3',
					borderRadius: '20px',
					p: fs.p,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					minWidth: `${fs.iconBox}px`,
					height: `${fs.iconBox}px`
				}}>
					{Icon && <Icon color={theme.accent} width={fs.iconSize} height={fs.iconSize} />}
				</Box>
			</Box>
		</Box>
	);
};

const MetricCard = ({ icon: Icon, label, value, unit, subtitle, sensorName, totalCards, theme }) => {
	const displayValue = formatMetricValue(value);
	const cardHeight = totalCards === 1 ? 350 : totalCards <= 4 ? 270 : 250;
	const isCompact = totalCards > 4;
	const sensorNameFontSize = isCompact ? 24 : 32;

	return (
		<Box
			sx={{
				backgroundColor: theme.cardBackground,
				borderRadius: '20px',
				px: { xs: 3, sm: 4, md: 5 },
				py: { xs: 3, sm: 3.5, md: 4 },
				border: '2px solid rgba(62, 95, 167, 0.15)',
				display: 'flex',
				flexDirection: 'column',
				gap: 2.5,
				minHeight: cardHeight,
				maxHeight: cardHeight,
				width: '100%'
			}}
		>
			<Typography sx={{ color: theme.sensorName, fontSize: sensorNameFontSize, fontWeight: 500, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
				{sensorName || label}
			</Typography>

			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flex: 1 }}>
				<Box sx={{ minWidth: 0 }}>
					<Typography sx={{ color: theme.cardLabel, fontSize: isCompact ? 22 : 26, fontWeight: 700, lineHeight: 1.3 }}>
						{subtitle || label}
					</Typography>
					<Typography sx={{ color: theme.cardValue, fontSize: 64, fontWeight: 700, lineHeight: 1.05 }}>
						{displayValue}
						{unit && (
							<Box component="span" sx={{ fontSize: 36, ml: 0.4 }}>
								{unit}
							</Box>
						)}
					</Typography>
				</Box>

				<Box sx={{
					backgroundColor: '#4A6BA3',
					borderRadius: '20px',
					p: 2.5,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					minWidth: '100px',
					height: '100px'
				}}>
					{Icon && <Icon color={theme.accent} width={56} height={56} />}
				</Box>
			</Box>
		</Box>
	);
};

function ORDashboard() {
	const { hospitalCode, roomCode } = useParams();

	const [orConfig, setOrConfig] = useState({ theme: getThemeFromDomain() ?? 'dips' });
	const theme = getORTheme(orConfig.theme);
	const appLogo = THEME_LOGOS[orConfig.theme] ?? dipsLogo;

	const [loading, setLoading] = useState(true);
	const [dashboardData, setDashboardData] = useState(null);
	const [error, setError] = useState(null);
	const [currentTime, setCurrentTime] = useState(new Date());
	const isInitialLoad = React.useRef(true);

	// Fetch theme config once on mount — domain takes priority, config.theme used as fallback (e.g. local dev)
	useEffect(() => {
		if (getThemeFromDomain()) return; // domain already resolved, skip API call
		fetch('/api/ordashboard/theme')
			.then(r => r.json())
			.then(res => { if (res.success) setOrConfig(res.data); })
			.catch(() => {}); // silently keep defaults on failure
	}, []);

	// Update time every second
	useEffect(() => {
		const timer = setInterval(() => setCurrentTime(new Date()), 1000);
		return () => clearInterval(timer);
	}, []);

	// Fetch dashboard data when room changes
	useEffect(() => {
		if (!hospitalCode || !roomCode) {
			setLoading(false);
			return;
		}

		const fetchDashboardData = async (isBackground = false) => {
			// Only show loading spinner on initial load, not on background updates
			if (!isBackground) {
				setLoading(true);
			}
			setError(null);
			try {
				const response = await fetch(
					`/api/ordashboard/hospital/${hospitalCode}/room/${roomCode}`,
					{ credentials: 'include' }
				);
				const result = await response.json();
				if (result.success) {
					setDashboardData(result.data);
				} else {
					setError(result.error || result.message || 'Failed to load dashboard data');
				}
			} catch (err) {
				setError('Error loading dashboard data');
				console.error('Error fetching dashboard data:', err);
			} finally {
				if (!isBackground) {
					setLoading(false);
				}
				isInitialLoad.current = false;
			}
		};

		// Initial load
		fetchDashboardData(false);
		
		// Refresh every 30 seconds for real-time display (background updates)
		const interval = setInterval(() => fetchDashboardData(true), 30000);
		return () => clearInterval(interval);
	}, [hospitalCode, roomCode]);

	const formatTime = (date) => {
		return date.toLocaleTimeString('en-US', { 
			hour: '2-digit', 
			minute: '2-digit',
			hour12: true 
		});
	};

	const formatDate = (date) => {
		return date.toLocaleDateString('en-US', { 
			weekday: 'short',
			month: 'short', 
			day: 'numeric',
			year: 'numeric'
		});
	};

	const temperatureSensors = dashboardData?.temperature?.sensors || [];
	const humiditySensors = dashboardData?.humidity?.sensors || [];
	const doorSensors = dashboardData?.doorOpenings?.sensors || [];
	const hasDoorCumulativeData = doorSensors.length > 0 && dashboardData?.doorOpenings?.cumulativeCount !== undefined;
	const totalSensors = temperatureSensors.length + humiditySensors.length + doorSensors.length + (hasDoorCumulativeData ? 1 : 0);

	return (
		<Box
			sx={{
				backgroundColor: theme.pageBackground,
				minHeight: '100dvh',
				boxSizing: 'border-box',
				overflowX: 'hidden',
				width: '100%',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'flex-start',
				alignItems: 'stretch'
			}}
		>
			<Box
				component="header"
				sx={{
					backgroundColor: theme.headerBackground,
					borderBottom: `1px solid ${theme.headerBorder}`,
					boxShadow: '0px 6px 6px 0px rgba(0, 0, 0, 0.5)',
					width: '100%',
					px: 4,
					py: 2.5,
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center'
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1, gap: 2 }}>
					<Box
						component="img"
						src={appLogo}
						alt={theme.appLogoAlt}
						sx={{ display: 'block', height: { xs: 36, sm: 46, md: 52 }, width: 'auto', objectFit: 'contain' }}
					/>
					<Typography sx={{ color: theme.textSecondary, fontSize: { xs: 16, sm: 20, md: 24 }, fontWeight: 600, letterSpacing: 0.5 }}>
						{theme.appLogoAlt}
					</Typography>
				</Box>
				{/* Room Name - Center */}
				<Box sx={{ flex: 2, display: 'flex', justifyContent: 'center' }}>
					<Typography sx={{ color: theme.textSecondary, fontSize: { xs: 20, sm: 28, md: 32 }, fontWeight: 700, letterSpacing: 0.2, textAlign: 'center' }}>
						{dashboardData?.room?.name || ''}
					</Typography>
				</Box>
				{/* Right spacer to balance layout */}
				<Box sx={{ flex: 1 }} />
				{/* Timer display */}
				{/* <Box sx={{ textAlign: 'right', flex: 1 }}>
					<Typography sx={{ color: theme.textSecondary, fontSize: 18 }}>
						{formatDate(currentTime)}
					</Typography>
					<Typography sx={{ color: theme.textPrimary, fontSize: 22, fontWeight: 600 }}>
						{formatTime(currentTime)}
					</Typography>
				</Box> */}
			</Box>

			<Box
				sx={{
					backgroundColor: theme.frameBackground,
					width: '94%',
					mx: 'auto',
					mt: '4vh',
					mb: '2vh',
					height: 'calc(100dvh - 130px - 4vh)',
					borderRadius: '16px',
					border: '2px solid rgba(167, 192, 236, 0.35)',
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column'
				}}
			>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						px: { xs: 3, sm: 4, md: 5 },
						py: { xs: 3, sm: 3.5, md: 4 },
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: totalSensors === 1 ? 'center' : 'flex-start',
						gap: { xs: 3, sm: 3.5, md: 4 },
						overflowY: 'auto',
						scrollbarWidth: 'thin',
						scrollbarColor: 'rgba(167, 192, 236, 0.55) transparent',
						'&::-webkit-scrollbar': { width: '6px' },
						'&::-webkit-scrollbar-track': { background: 'transparent' },
						'&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(167, 192, 236, 0.55)', borderRadius: '999px' }
					}}
				>
					{loading && (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
							<CircularProgress sx={{ color: theme.accent }} />
						</Box>
					)}

					{error && !loading && (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
							<Typography sx={{ color: theme.error }}>{String(error)}</Typography>
						</Box>
					)}

					{!loading && !error && dashboardData && (
						<Box sx={{ 
							display: 'flex',
							flexWrap: 'wrap',
							justifyContent: 'center',
							gap: { xs: 3, sm: 3, md: 3 }, 
							maxWidth: totalSensors === 1 ? '650px' : totalSensors <= 4 ? '1100px' : '1600px', 
							width: '100%', 
							mx: 'auto',
							my: totalSensors <= 6 ? 'auto' : 0,
							'& > *': {
								flex: totalSensors === 1 
									? '0 0 100%' 
									: totalSensors <= 4 
										? { xs: '0 0 100%', sm: '0 0 calc(50% - 12px)' }
										: { xs: '0 0 100%', sm: '0 0 calc(50% - 12px)', md: '0 0 calc(33.333% - 16px)' },
								maxWidth: totalSensors === 1 
									? '650px' 
									: totalSensors <= 4 
										? { xs: '100%', sm: 'calc(50% - 12px)' }
										: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' }
							}
						}}>
							{/* Temperature Sensors */}
							{temperatureSensors.map((sensor) => (
								<MetricCard
									key={`temp-${sensor.id}`}
									icon={TemperatureIcon}
									label="Temperature"
									value={sensor.value !== null && sensor.value !== undefined ? Math.round(Number(sensor.value)) : null}
									unit={dashboardData.temperature?.unit}
									sensorName={sensor.name}
									totalCards={totalSensors}
									theme={theme}
								/>
							))}
							
							{/* Humidity Sensors */}
							{humiditySensors.map((sensor) => (
								<MetricCard
									key={`humidity-${sensor.id}`}
									icon={humidityIcon}
									label="Humidity"
									value={sensor.value !== null && sensor.value !== undefined ? Math.round(Number(sensor.value)) : null}
									unit={dashboardData.humidity?.unit}
									sensorName={sensor.name}
									totalCards={totalSensors}
									theme={theme}
								/>
							))}
							
							{/* Door Sensors */}
							{doorSensors.map((sensor) => (
								<MetricCard
									key={`door-${sensor.id}`}
									icon={DoorIcon}
									label="Openings/Day"
									value={sensor.count}
									sensorName={sensor.name}
									totalCards={totalSensors}
									theme={theme}
								/>
							))}
							
							{/* Door Openings Cumulative */}
							{doorSensors.length > 0 && dashboardData?.doorOpenings?.cumulativeCount !== undefined && (
								<DoorCumulativeCard
									icon={DoorIcon}
									cumulativeCount={dashboardData.doorOpenings.cumulativeCount}
									cumulativeDuration={dashboardData.doorOpenings.cumulativeDuration}
									avgDuration={dashboardData.doorOpenings.avgDuration}
									totalCards={totalSensors}
									theme={theme}
								/>
							)}
						</Box>
					)}

					{!loading && (!hospitalCode || !roomCode) && (
						<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, gap: 2, px: 3 }}>
							<Typography sx={{ color: theme.textPrimary, fontSize: 20, fontWeight: 600, textAlign: 'center' }}>
								Hospital and Room Required
							</Typography>
							<Typography sx={{ color: theme.textSecondary, fontSize: 16, textAlign: 'center' }}>
								Please navigate to a specific room dashboard using the URL format:
							</Typography>
							<Typography sx={{ color: theme.accent, fontSize: 14, fontFamily: 'monospace', textAlign: 'center', backgroundColor: 'rgba(0, 0, 0, 0.3)', px: 2, py: 1, borderRadius: 1 }}>
								/ordashboard/&lt;hospitalCode&gt;/&lt;roomCode&gt;
							</Typography>
							<Typography sx={{ color: theme.textSecondary, fontSize: 14, textAlign: 'center', fontStyle: 'italic' }}>
								Example: /ordashboard/HOSPITAL-CODE/ROOM-CODE
							</Typography>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
}

export default ORDashboard;
